import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Mail, FileText, MessageSquare, Eye, Inbox } from 'lucide-react';
import { api } from '@/lib/api';
import type {
  ContactMessage,
  BlogPostSummary,
  NewsletterSubscriber,
  Lead,
} from '@inafana/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface AnalyticsSummary {
  totalInWindow: number;
  totalAllTime: number;
}

export function Dashboard(): React.JSX.Element {
  const leads = useQuery({
    queryKey: ['admin', 'leads'],
    queryFn: () => api<{ leads: Lead[] }>('/admin/leads'),
  });
  const messages = useQuery({
    queryKey: ['admin', 'messages'],
    queryFn: () => api<{ messages: ContactMessage[] }>('/admin/messages'),
  });
  const blog = useQuery({
    queryKey: ['admin', 'blog'],
    queryFn: () => api<{ posts: BlogPostSummary[] }>('/admin/blog'),
  });
  const newsletter = useQuery({
    queryKey: ['admin', 'newsletter'],
    queryFn: () => api<{ subscribers: NewsletterSubscriber[] }>('/admin/newsletter'),
  });
  const analytics = useQuery({
    queryKey: ['admin', 'analytics', 'summary', 30],
    queryFn: () =>
      api<AnalyticsSummary>('/admin/analytics/summary', { query: { days: 30 } }),
  });

  const newLeads = leads.data?.leads.filter((l) => l.status === 'NEW').length ?? 0;
  const unread = messages.data?.messages.filter((m) => !m.isRead).length ?? 0;

  const stats = [
    {
      label: 'New leads',
      value: newLeads,
      icon: Inbox,
      to: '/admin/leads',
    },
    {
      label: 'Unread messages',
      value: unread,
      icon: MessageSquare,
      to: '/admin/messages',
    },
    {
      label: 'Blog posts',
      value: blog.data?.posts.length ?? 0,
      icon: FileText,
      to: '/admin/blog',
    },
    {
      label: 'Newsletter subs',
      value: newsletter.data?.subscribers.length ?? 0,
      icon: Mail,
      to: '/admin/newsletter',
    },
    {
      label: 'Page views (30d)',
      value: analytics.data?.totalInWindow ?? 0,
      icon: Eye,
      to: '/admin/analytics',
    },
  ];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Overview of recent activity.</p>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {stats.map(({ label, value, icon: Icon, to }) => (
          <Link key={label} to={to}>
            <Card className="transition-colors hover:bg-accent/30">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold">{value.toLocaleString()}</div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
